import { useState, useMemo, useCallback } from 'react';
import { Table, Select, Button, Modal, Input, message, Tag, Typography, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { DEBOUNCE_DELAY } from '@/constants';

function debounce(fn: (v: string) => void, delay: number) {
  let timer: ReturnType<typeof setTimeout>;
  return (v: string) => { clearTimeout(timer); timer = setTimeout(() => fn(v), delay); };
}
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  getModerationStudents,
  suspendStudent,
  unsuspendStudent,
  type ModerationStudent,
} from '@/services/onboarding.service';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { STATUS_COLORS } from '@/constants';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface SuspendModalState {
  open: boolean;
  studentId: string;
  studentName: string;
}

export default function StudentModerationPage() {
  const [page, setPage] = useState(1);
  const [emailVerified, setEmailVerified] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const debouncedSearch = useMemo(
    () => debounce((value: string) => { setSearch(value); setPage(1); }, DEBOUNCE_DELAY),
    [],
  );
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => debouncedSearch(e.target.value),
    [debouncedSearch],
  );
  const [suspendModal, setSuspendModal] = useState<SuspendModalState>({
    open: false,
    studentId: '',
    studentName: '',
  });
  const [suspendReason, setSuspendReason] = useState('');
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const queryParams = {
    ...(emailVerified !== 'all' && { emailVerified: emailVerified === 'true' }),
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(search.trim() && { search: search.trim() }),
    page,
    limit: 20,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['moderation-students', emailVerified, statusFilter, search, page],
    queryFn: () => getModerationStudents(queryParams),
  });

  const students: ModerationStudent[] = data?.data?.students ?? [];
  const total: number = data?.data?.total ?? 0;

  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      suspendStudent(id, reason),
    onSuccess: () => {
      message.success('Student suspended');
      setSuspendModal({ open: false, studentId: '', studentName: '' });
      setSuspendReason('');
      queryClient.invalidateQueries({ queryKey: ['moderation-students'] });
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? 'Failed to suspend student');
    },
  });

  const unsuspendMutation = useMutation({
    mutationFn: (id: string) => unsuspendStudent(id),
    onSuccess: () => {
      message.success('Student unsuspended');
      queryClient.invalidateQueries({ queryKey: ['moderation-students'] });
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? 'Failed to unsuspend student');
    },
  });

  const handleSuspendConfirm = () => {
    if (!suspendReason.trim()) {
      message.warning('Please provide a reason for suspension');
      return;
    }
    if (suspendReason.trim().length < 10) {
      message.warning('Suspension reason must be at least 10 characters');
      return;
    }
    suspendMutation.mutate({ id: suspendModal.studentId, reason: suspendReason.trim() });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => <span style={{ fontWeight: 600, color: '#0f172a' }}>{v}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v: string) => (
        <span style={{ color: '#64748b', fontFamily: 'monospace', fontSize: 12 }}>
          {v}
        </span>
      ),
    },
    {
      title: 'Email Verified',
      dataIndex: 'emailVerified',
      key: 'emailVerified',
      render: (v: boolean) => (
        <Tag color={v ? 'success' : 'warning'} style={{ borderRadius: 20 }}>
          {v ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Parent Email',
      key: 'parentEmail',
      render: (_: unknown, record: ModerationStudent) => {
        if (record.parentEmail) {
          return (
            <span style={{ color: '#64748b', fontFamily: 'monospace', fontSize: 12 }}>
              {record.parentEmail}
            </span>
          );
        }
        return <Tag color="warning" style={{ borderRadius: 20 }}>Not provided</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => {
        const color = STATUS_COLORS[v] ?? STATUS_COLORS.INACTIVE;
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
            color: '#fff',
            background: color,
          }}>
            {v}
          </span>
        );
      },
    },
    {
      title: 'Signed Up',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => (
        <span style={{ color: '#94a3b8', fontSize: 12 }}>{dayjs(v).format('DD MMM YYYY')}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: ModerationStudent) => {
        if (record.status === 'SUSPENDED') {
          return (
            <Button
              size="small"
              onClick={() => unsuspendMutation.mutate(record.id)}
              loading={unsuspendMutation.isPending}
            >
              Unsuspend
            </Button>
          );
        }
        return (
          <Button
            danger
            size="small"
            onClick={() => setSuspendModal({ open: true, studentId: record.id, studentName: record.name })}
          >
            Suspend
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: '#0f172a' }}>Student Moderation</Title>
        <Text style={{ color: '#64748b', fontSize: 13 }}>
          Review and moderate student accounts
        </Text>
      </div>

      {/* Filter row */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by name…"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          onChange={handleSearchChange}
          allowClear
          onClear={() => { setSearch(''); setPage(1); }}
          style={{ flex: '1 1 180px', maxWidth: 260, height: 32 }}
        />
        <div>
          <Text style={{ fontSize: 12, color: '#64748b', marginRight: 6 }}>Email Verified:</Text>
          <Select
            value={emailVerified}
            onChange={(v) => { setEmailVerified(v); setPage(1); }}
            style={{ width: 120 }}
            options={[
              { value: 'all', label: 'All' },
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
          />
        </div>
        <div>
          <Text style={{ fontSize: 12, color: '#64748b', marginRight: 6 }}>Status:</Text>
          <Select
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            style={{ width: 130 }}
            options={[
              { value: 'all', label: 'All' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'SUSPENDED', label: 'Suspended' },
              { value: 'INACTIVE', label: 'Inactive' },
            ]}
          />
        </div>
      </Space>

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isLoading ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
          ) : students.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>No students found</div>
          ) : students.map((r) => {
            const statusColor = STATUS_COLORS[r.status] ?? STATUS_COLORS.INACTIVE;
            return (
              <div key={r.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{r.name}</div>
                    <div style={{ color: '#64748b', fontSize: 12, fontFamily: 'monospace' }}>{r.email}</div>
                  </div>
                  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: '#fff', background: statusColor }}>
                    {r.status}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid #f1f5f9', alignItems: 'center' }}>
                  <Tag color={r.emailVerified ? 'success' : 'warning'} style={{ borderRadius: 20, margin: 0 }}>
                    Email: {r.emailVerified ? 'Verified' : 'Unverified'}
                  </Tag>
                  <Tag color={r.parentEmailConfirmed ? 'success' : 'warning'} style={{ borderRadius: 20, margin: 0 }}>
                    Parent: {r.parentEmailConfirmed ? 'Confirmed' : 'Pending'}
                  </Tag>
                  <span style={{ marginLeft: 'auto' }}>
                    {r.status === 'SUSPENDED' ? (
                      <Button size="small" onClick={() => unsuspendMutation.mutate(r.id)} loading={unsuspendMutation.isPending}>
                        Unsuspend
                      </Button>
                    ) : (
                      <Button danger size="small" onClick={() => setSuspendModal({ open: true, studentId: r.id, studentName: r.name })}>
                        Suspend
                      </Button>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={students}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            total,
            pageSize: 20,
            onChange: setPage,
            showTotal: (t) => `${t} student${t !== 1 ? 's' : ''}`,
          }}
          scroll={{ x: 'max-content' }}
          style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
        />
      )}

      <Modal
        title={`Suspend ${suspendModal.studentName}`}
        open={suspendModal.open}
        onCancel={() => { setSuspendModal({ open: false, studentId: '', studentName: '' }); setSuspendReason(''); }}
        onOk={handleSuspendConfirm}
        confirmLoading={suspendMutation.isPending}
        okText="Suspend Student"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 8 }}>
          <Text style={{ color: '#64748b', fontSize: 13 }}>
            Provide a reason for suspending this student account. This will be logged in the audit trail.
          </Text>
        </div>
        <TextArea
          value={suspendReason}
          onChange={(e) => setSuspendReason(e.target.value)}
          placeholder="Enter suspension reason…"
          rows={3}
          maxLength={500}
          showCount
          style={{ marginTop: 8 }}
        />
      </Modal>
    </div>
  );
}
